import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, RotateCcw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Capture non-PHI error context for diagnostics.
 */
function captureErrorContext(): Record<string, unknown> {
  return {
    timestamp: new Date().toISOString(),
    route: window.location.pathname,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    online: navigator.onLine,
    buildVersion: import.meta.env.VITE_BUILD_VERSION || 'dev',
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Display name for the route in error messages */
  routeName?: string;
  /** Path to navigate back to on error */
  fallbackPath?: string;
  /** Label for the fallback navigation button */
  fallbackLabel?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Generic Route-level Error Boundary.
 *
 * GUARANTEES:
 * - Never renders blank screen on error
 * - Always provides recovery actions
 * - Captures non-PHI diagnostics
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    const context = captureErrorContext();
    console.error('[RouteErrorBoundary] Crash caught:', {
      route: this.props.routeName || 'unknown',
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      context,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleNavigateBack = (): void => {
    const { fallbackPath } = this.props;
    if (fallbackPath) {
      window.location.href = fallbackPath;
    } else {
      window.history.back();
    }
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { routeName = 'Page', fallbackLabel = 'Go Back' } = this.props;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-destructive/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-xl">{routeName} Error</CardTitle>
              <CardDescription>
                An unexpected error occurred while loading this page.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <details className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                <summary className="cursor-pointer font-medium">Technical Details</summary>
                <pre className="mt-2 whitespace-pre-wrap break-all">
                  {this.state.error?.name}: {this.state.error?.message}
                </pre>
              </details>

              <div className="text-sm text-muted-foreground">
                <p>This may have been caused by:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Network connectivity issues</li>
                  <li>Session expiration</li>
                  <li>Browser compatibility issues</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button
                onClick={this.handleReset}
                className="w-full gap-2"
                variant="default"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </Button>

              <Button
                onClick={this.handleNavigateBack}
                variant="outline"
                className="w-full gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {fallbackLabel}
              </Button>

              <Button
                onClick={this.handleGoHome}
                variant="ghost"
                className="w-full gap-2"
              >
                <Home className="w-4 h-4" />
                Return to Home
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
